import { IconButton, styled } from '@ethings/ui-toolkit';
import { Box, SxProps, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { FC, useMemo, useState } from 'react';
import { useSmallScreen } from '../../hooks';
import { BaseLayers, ToggleLayers } from '../../types';
import { useFlags } from '../flags';
import { Icon } from '../icons';
import { useMapLayers } from '../map/hooks';
import { mapTemplateTypeToToggleLayer } from './layer-mapping-new';
import { LayerMenuContent } from './layer-menu-content';
import { LayerMenuDesktop } from './layer-menu-desktop';
import { LayerMenuMobile } from './layer-menu-mobile';
import { BaseLayerDefinition, DetailLayerDefinition, EntityLayerDefinition } from './layer-menu.types';

const LayerButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.contrastText,
  boxShadow: theme.shadows[3],
  '&:hover': {
    backgroundColor: theme.palette.grey['50'],
  },
}));

interface Props {
  baseLayers: BaseLayerDefinition[];
  detailLayers: DetailLayerDefinition[];
  entityLayers: EntityLayerDefinition[];
  handleBaseChange: (label: BaseLayers) => void;
  selectedBaseLayer: BaseLayers;
  disabledLayers?: ToggleLayers[];
  buttonSx?: SxProps<Theme>;
  shouldShowLayerSelection?: boolean;
  zoomLevel: number;
  mapWidth: number;
  mapHeight: number;
}

export const LayerMenu: FC<Props> = ({
  baseLayers: templateBaseLayers,
  buttonSx,
  detailLayers: templateDetailLayers,
  entityLayers: templateEntityLayers,
  handleBaseChange,
  selectedBaseLayer,
  disabledLayers = [],
  shouldShowLayerSelection = true,
  zoomLevel,
  mapWidth,
  mapHeight,
}) => {
  const flags = useFlags();
  const smallScreen = useSmallScreen();
  const { selectedLayers, setSelectedLayers } = useMapLayers();

  const [open, setOpen] = useState(false);

  const baseLayers = useMemo(() => templateBaseLayers.filter((layer) => !layer.flag || flags[layer.flag]),
    [templateBaseLayers, flags],
  );

  const detailLayers = useMemo(() => templateDetailLayers.filter((layer) => !layer.flag || flags[layer.flag]),
    [templateDetailLayers, flags],
  );

  const entityLayers = useMemo(() => templateEntityLayers.filter((layer) => !layer.flag || flags[layer.flag]),
    [templateEntityLayers, flags],
  );

  const isDetailLayerChecked = (type: string): boolean => {
    if (type === 'things') return false;
    const toggleLayer = mapTemplateTypeToToggleLayer(type);
    if (toggleLayer && disabledLayers.includes(toggleLayer)) return false;
    return toggleLayer ? selectedLayers.includes(toggleLayer) : false;
  };

  const isEntityLayerChecked = (type: string): boolean => {
    const toggleLayer = mapTemplateTypeToToggleLayer(type);
    if (toggleLayer && disabledLayers.includes(toggleLayer)) return false;
    return toggleLayer ? selectedLayers.includes(toggleLayer) : false;
  };

  const handleDetailLayerChange = (type: string): void => {
    if (type === 'things') return;

    const toggleLayer = mapTemplateTypeToToggleLayer(type);
    if (!toggleLayer) return;
    if (disabledLayers.includes(toggleLayer)) return;

    if (selectedLayers.includes(toggleLayer)) {
      setSelectedLayers(selectedLayers.filter((item) => item !== toggleLayer));
    } else {
      setSelectedLayers([...selectedLayers, toggleLayer]);
    }
  };

  const handleEntityLayerChange = (type: string, enabled: boolean): void => {
    const toggleLayer = mapTemplateTypeToToggleLayer(type);
    if (!toggleLayer) return;
    if (disabledLayers.includes(toggleLayer)) return;

    if (enabled) {
      if (!selectedLayers.includes(toggleLayer)) {
        setSelectedLayers([...selectedLayers, toggleLayer]);
      }
    } else {
      setSelectedLayers(selectedLayers.filter((item) => item !== toggleLayer));
    }
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  if (!shouldShowLayerSelection) return null;

  const defaultButtonSx = (theme: Theme): SystemStyleObject<Theme> => ({
    position: 'absolute',
    right: theme.spacing(2),
    bottom: 116,
    zIndex: 500,
  });

  const content = (
    <LayerMenuContent
      baseLayers={baseLayers}
      detailLayers={detailLayers}
      entityLayers={entityLayers}
      disabledLayers={disabledLayers}
      handleBaseChange={handleBaseChange}
      handleDetailLayerChange={handleDetailLayerChange}
      handleEntityLayerChange={handleEntityLayerChange}
      isDetailLayerChecked={isDetailLayerChecked}
      isEntityLayerChecked={isEntityLayerChecked}
      selectedBaseLayer={selectedBaseLayer}
      zoomLevel={zoomLevel}
      mapWidth={mapWidth}
      mapHeight={mapHeight}
    />
  );

  const buttonElement = (
    <Box sx={buttonSx ?? defaultButtonSx}>
      <LayerButton color='primary' data-testid="inlay-map-layers-button" onClick={() => setOpen(!open)}>
        <Icon data-testid="layers-icon" icon="Layers" />
      </LayerButton>
    </Box>
  );

  return <>
    {smallScreen ? (
      <>
        {buttonElement}
        <LayerMenuMobile onClose={handleClose} open={open}>
          {content}
        </LayerMenuMobile>
      </>
    ) : (
      <LayerMenuDesktop buttonElement={buttonElement} onClose={handleClose} open={open}>
        {content}
      </LayerMenuDesktop>
    )}
  </>;
};
